import React, { FC, useState } from 'react';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import BasicButton from '../buttons/basicButton';
import RadioInput from './radioInput';

export interface RadioSelectOption {
    label: string;
    value: string;
}

interface RadioSelectScrollableProps {
    options: RadioSelectOption[];
    selected: string;
    onApply: (value: string) => void;
    searchPlaceholder?: string;
}

const RadioSelectScrollable: FC<RadioSelectScrollableProps> = ({
    options,
    selected,
    onApply,
    searchPlaceholder = 'Search...',
}) => {
    const [searchValue, setSearchValue] = useState('');

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    const [selectedOption, setSelectedOption] = useState<string>(selected);

    return (
        <Wrapper>
            <SearchInput
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
            />
            <RadioList>
                {filteredOptions.map((opt) => (
                    <StyledRadioInput
                        key={opt.value}
                        value={opt.value}
                        selectedOption={selectedOption}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        descriptionText={opt.label}
                    />
                ))}
            </RadioList>
            <ApplyButton
                name="Apply"
                isPrimary
                type="button"
                action={() => onApply(selectedOption)}
                disabled={!selectedOption}
            />
        </Wrapper>
    );
};

export default RadioSelectScrollable;

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '240px',
    padding: aampeTheme.spacing4(4),
    gap: aampeTheme.spacing4(6),
    borderRadius: aampeTheme.borderRadius.medium,
    background: aampeTheme.colors.white,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.10)',
});

const SearchInput = styled.input({
    display: 'flex',
    height: '36px',
    padding: aampeTheme.spacing4(1, 2),
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
    backgroundColor: aampeTheme.colors.backgroundWG02,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    color: aampeTheme.colors.textAccessibleWG01,
    borderRadius: aampeTheme.borderRadius.default,
    fontWeight: aampeTheme.weights.light,
});

const RadioList = styled.div({
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    alignItems: 'flex-start',
    gap: aampeTheme.spacing4(3),
    alignSelf: 'stretch',
    height: '120px',
});

const StyledRadioInput = styled(RadioInput)({
    padding: 0,
    height: '18px',
    display: 'flex',
    gap: aampeTheme.spacing4(2),
    justifyContent: 'center',

    // Target the MUI radio button
    '& .MuiRadio-root': {
        width: '20px',
        height: '20px',
    },

    // Target the description text
    '& p': {
        fontSize: aampeTheme.size.font.textMedium,
    },

    // Target the RadioButtonContent wrapper (if it's the second child)
    '& .radio-content': {
        padding: 0,
    },
});

const ApplyButton = styled(BasicButton)({
    width: '100%',
    justifyContent: 'center',
    borderRadius: aampeTheme.borderRadius.default,
    fontWeight: aampeTheme.weights.heavy,
    fontSize: aampeTheme.size.font.textMedium,
});
